import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert, Button, Input, InputGroup, Modal, ModalBody, ModalFooter, ModalHeader, Spinner,
} from "reactstrap";
import {
  AlertTriangle, Camera, CheckCircle, Image as ImageIcon, Search, X as XIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageFrame, PageBody } from "../../Components/Common/EmbeddablePage";
import {
  getHazardPaths, getTargets, getPhotos, getPhotoUrl, uploadPhoto, saveAnswer,
  updateFilledForm, createDraftForm, discardDraftForm, getMyDrafts,
} from "../../services/sahaInspectionService";
import i18n, { currentLocaleTag } from "../../i18n";

// ─────────────────────────────────────────────────────────────────────────────
// /FdsSaha/Formlar/uygunsuzluk-olustur  (#603, backend #604)
//
// Saha denetçisi için hızlı Uygunsuzluk formu: doğrudan açık bir modal.
//   1. Fotoğraflar (kamera + galeri, birden çok)  2. Birim  3. Form Türü (Kapsam) + arama
//   4. Stand / salon (seçilen Kapsam'ın target_entity'sine göre)
// Kayıt akışı her zaman taslak-önce: POST /form {isDraft:true} → foto POST'ları → PUT /form/content →
// POST /form/{id}/partial {isDraft:false, …}. Yarıda kalırsa form taslak olarak durur ("Taslaklarınız").
// Uygunsuzluk (hazard) seçilir; Tehlike / Risk bu akışta seçilmez (#604 kararı).
// ─────────────────────────────────────────────────────────────────────────────

const MAX_HITS = 30;
const MAX_TARGETS = 60;
const MAX_SIDE = 1600;

// Türkçe büyük/küçük ve aksan farkını yok sayan arama gövdesi ("isg" → "İSG", "uygunsuzluk" → "Uygunsuzluk")
const fold = (s) => String(s || "")
  .toLocaleLowerCase("tr")
  .replace(/ı/g, "i")
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "");

// Telefon fotoğrafları 5-10 MB; saha ağında yüklemeyi hızlandırmak için uzun kenar 1600 px'e indirilir.
// Herhangi bir adım başarısız olursa orijinal dosya yüklenir.
async function shrinkImage(file) {
  if (!file.type.startsWith("image/") || file.size < 700 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    if (bmp.close) bmp.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

const deptKeyOf = (row) => row.departmentId ?? 0;

const typeOf = (row) => ({
  typeId:       row.typeId,
  typeName:     row.typeName,
  scopeId:      row.scopeId,
  scopeName:    row.scopeName,
  targetEntity: row.targetEntity,
  deptKey:      deptKeyOf(row),
});

function UygunsuzlukOlusturPage() {
  const { t } = useTranslation("saha");
  const navigate = useNavigate();
  const location = useLocation();

  const [open,      setOpen]      = useState(true);
  const [paths,     setPaths]     = useState(null);   // null = yükleniyor
  const [loadError, setLoadError] = useState(false);
  const [deptId,    setDeptId]    = useState(null);
  const [type,      setType]      = useState(null);
  const [hazard,    setHazard]    = useState(null);
  const [query,     setQuery]     = useState("");
  const [targets,   setTargets]   = useState({});     // targetEntity → liste
  const [target,    setTarget]    = useState(null);
  const [targetQ,   setTargetQ]   = useState("");
  const [staged,    setStaged]    = useState([]);     // henüz yüklenmemiş: { key, file, url }
  const [uploaded,  setUploaded]  = useState([]);     // sunucudaki foto dokümanları
  const [draftId,   setDraftId]   = useState(null);
  const [drafts,    setDrafts]    = useState([]);
  const [busy,      setBusy]      = useState(null);   // null | { done, total }
  const [error,     setError]     = useState(null);
  const [result,    setResult]    = useState(null);   // null | "saved" | "draft"
  const [confirmClose, setConfirmClose] = useState(false);

  const cameraRef   = useRef(null);
  const galleryRef  = useRef(null);
  const targetsLoad = useRef({});
  const stagedRef   = useRef([]);

  const fuarId = Number(localStorage.getItem("activeFairId") || 3);
  const userId = Number(localStorage.getItem("userId") || 17);

  const title = t("uygunsuzlukOlusturPage.baslik");

  useEffect(() => {
    document.title = i18n.t("saha:sahaFormFill.fdsSaha", { title: i18n.t("saha:uygunsuzlukOlusturPage.baslik") });
  }, []);

  // Katalog (bir kez) + çağıranın açık taslakları
  useEffect(() => {
    let alive = true;
    getHazardPaths()
      .then((rows) => { if (alive) setPaths(rows); })
      .catch((err) => {
        console.error("[UygunsuzlukOlusturPage] hazard-paths:", err);
        if (alive) { setPaths([]); setLoadError(true); }
      });
    getMyDrafts({ fuarId: Number(localStorage.getItem("activeFairId") || 3) })
      .then((rows) => { if (alive) setDrafts(rows); });
    return () => { alive = false; };
  }, []);

  useEffect(() => { stagedRef.current = staged; }, [staged]);
  useEffect(() => () => { stagedRef.current.forEach((p) => URL.revokeObjectURL(p.url)); }, []);

  // ── Katalogdan türetilenler ───────────────────────────────────────────────
  const rows = useMemo(
    () => (paths || []).map((r) => ({
      ...r,
      hay: fold(`${r.hazardName} ${r.questionName} ${r.categoryName} ${r.typeName} ${r.scopeName} ${r.departmentName}`),
    })),
    [paths],
  );

  const departments = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      const k = deptKeyOf(r);
      if (!seen.has(k)) seen.set(k, r.departmentName || t("uygunsuzlukOlusturPage.digerBirim"));
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [rows, t]);

  const activeDept = deptId ?? (departments.length === 1 ? departments[0].id : null);

  const types = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (deptKeyOf(r) === activeDept && !seen.has(r.typeId)) seen.set(r.typeId, typeOf(r));
    });
    return [...seen.values()];
  }, [rows, activeDept]);

  const searching = fold(query).trim() !== "";
  const hits = useMemo(() => {
    const tokens = fold(query).split(/\s+/).filter(Boolean);
    return tokens.length === 0 ? [] : rows.filter((r) => tokens.every((tok) => r.hay.includes(tok)));
  }, [rows, query]);

  const typeGroups = useMemo(() => {
    if (!type) return [];
    const cats = new Map();
    rows.filter((r) => r.typeId === type.typeId).forEach((r) => {
      if (!cats.has(r.categoryId)) cats.set(r.categoryId, { id: r.categoryId, name: r.categoryName, questions: new Map() });
      const cat = cats.get(r.categoryId);
      if (!cat.questions.has(r.questionId)) cat.questions.set(r.questionId, { id: r.questionId, name: r.questionName, hazards: [] });
      cat.questions.get(r.questionId).hazards.push(r);
    });
    return [...cats.values()].map((c) => ({ ...c, questions: [...c.questions.values()] }));
  }, [rows, type]);

  // ── Hedef (stand / salon / …) ─────────────────────────────────────────────
  const entity     = type?.targetEntity || null;
  const entityList = entity ? targets[entity] : null;
  const entityLabel = {
    stand:      t("sahaFormFill.stand"),
    salon:      t("sahaFormFill.salon"),
    fuar_alani: t("sahaFormFill.fuarAlani"),
    fuar:       t("sahaFormFill.fuar"),
  }[entity] || "";
  // Fuar kapsamında seçilecek tek bir hedef var
  const activeTarget = target ?? (entity === "fuar" && entityList?.length === 1 ? entityList[0] : null);

  const targetTokens = fold(targetQ).split(/\s+/).filter(Boolean);
  const shownTargets = (entityList || []).filter((x) => {
    if (targetTokens.length === 0) return true;
    const hay = fold(`${x.name} ${x.firma || ""} ${x.salon || ""}`);
    return targetTokens.every((tok) => hay.includes(tok));
  });

  function ensureTargets(ent) {
    if (!ent || targetsLoad.current[ent]) return;
    targetsLoad.current[ent] = getTargets(ent).then((list) => {
      setTargets((prev) => ({ ...prev, [ent]: list }));
    });
  }

  // ── Seçimler ──────────────────────────────────────────────────────────────
  function pickDept(id) {
    if (id === activeDept) return;
    setDeptId(id);
    setType(null);
    setHazard(null);
    setTarget(null);
    setQuery("");
  }

  function pickType(next) {
    if (type?.targetEntity !== next.targetEntity) { setTarget(null); setTargetQ(""); }
    setType(next);
    setHazard(null);
    ensureTargets(next.targetEntity);
  }

  function pickHazard(row) {
    const next = typeOf(row);
    if (type?.targetEntity !== next.targetEntity) { setTarget(null); setTargetQ(""); }
    setDeptId(next.deptKey);
    setType(next);
    setHazard(row);
    setQuery("");
    ensureTargets(next.targetEntity);
  }

  // ── Fotoğraflar ───────────────────────────────────────────────────────────
  function addFiles(fileList) {
    const files = Array.from(fileList || []).filter((f) => !f.type || f.type.startsWith("image/"));
    if (files.length === 0) return;
    setStaged((prev) => [
      ...prev,
      ...files.map((file) => ({
        key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
  }

  function removeStaged(key) {
    const p = staged.find((x) => x.key === key);
    if (p) URL.revokeObjectURL(p.url);
    setStaged((prev) => prev.filter((x) => x.key !== key));
  }

  // ── Kayıt ─────────────────────────────────────────────────────────────────
  // Her zaman taslak-önce: yarıda kalan bir kayıt kayıp değil, taslak olur. true = tamamlandı.
  async function persist(finalize) {
    setError(null);
    const toUpload = staged;
    setBusy({ done: 0, total: toUpload.length });
    try {
      let id = draftId;
      if (!id) {
        const form = await createDraftForm({ fuarId, userId });
        id = form?.id;
        if (!id) throw new Error("draft form has no id");
        setDraftId(id);
      }

      const sent = [];
      const sentKeys = new Set();
      let tried = 0;
      for (const p of toUpload) {
        try {
          const doc = await uploadPhoto(id, await shrinkImage(p.file), { fuarId, inspectionLevelId: 2 });
          sent.push(doc);
          sentKeys.add(p.key);
        } catch (err) {
          console.error("[UygunsuzlukOlusturPage] uploadPhoto:", err);
        }
        tried += 1;
        setBusy({ done: tried, total: toUpload.length });
      }
      if (sent.length > 0) {
        toUpload.filter((p) => sentKeys.has(p.key)).forEach((p) => URL.revokeObjectURL(p.url));
        setUploaded((prev) => [...prev, ...sent]);
        setStaged((prev) => prev.filter((p) => !sentKeys.has(p.key)));
      }
      if (sentKeys.size < toUpload.length) {
        setError(t("uygunsuzlukOlusturPage.fotografHatasi", { count: toUpload.length - sentKeys.size }));
        return false;
      }

      if (finalize) {
        await saveAnswer(
          id, hazard.questionId,
          { value: 2, categoryId: hazard.categoryId, hazardId: hazard.hazardId },
          2, { strict: true },
        );
        await updateFilledForm(id, {
          isDraft:           false,
          inspectionTypeId:  type.typeId,
          inspectionScopeId: type.scopeId,
          scopeTargetId:     activeTarget.id,
          firmaId:           activeTarget.firmaId || null,
          sourceQuestionId:  hazard.questionId,
          sourceCategoryId:  hazard.categoryId,
        });
      }
      setResult(finalize ? "saved" : "draft");
      return true;
    } catch (err) {
      console.error("[UygunsuzlukOlusturPage] persist:", err);
      setError(t("uygunsuzlukOlusturPage.kayitHatasi"));
      return false;
    } finally {
      setBusy(null);
    }
  }

  // ── Taslaklar ─────────────────────────────────────────────────────────────
  async function resumeDraft(d) {
    setError(null);
    setDraftId(d.id);
    setDrafts((prev) => prev.filter((x) => x.id !== d.id));
    setUploaded(await getPhotos(d.id));
  }

  async function dropDraft(d) {
    if (!window.confirm(t("uygunsuzlukOlusturPage.taslakSilOnay"))) return;
    try {
      await discardDraftForm(d.id);
      setDrafts((prev) => prev.filter((x) => x.id !== d.id));
    } catch (err) {
      console.error("[UygunsuzlukOlusturPage] discard:", err);
      setError(t("uygunsuzlukOlusturPage.taslakSilinemedi"));
    }
  }

  // ── Kapat / sıfırla ───────────────────────────────────────────────────────
  function leave() {
    setOpen(false);
    if (location.key !== "default") navigate(-1);
    else navigate("/FdsSaha");
  }

  function requestClose() {
    if (busy) return;
    if (staged.length > 0 && !result) setConfirmClose(true);
    else leave();
  }

  function resetAll() {
    staged.forEach((p) => URL.revokeObjectURL(p.url));
    setStaged([]);
    setUploaded([]);
    setDraftId(null);
    setHazard(null);
    setTarget(null);
    setTargetQ("");
    setQuery("");
    setError(null);
    setResult(null);
    getMyDrafts({ fuarId }).then(setDrafts);
  }

  // ── Görünüm ───────────────────────────────────────────────────────────────
  const canSave  = !!hazard && !!activeTarget && !busy;
  const canDraft = staged.length > 0 && !busy;
  const fmtDate  = (iso) => (iso
    ? new Date(iso).toLocaleString(currentLocaleTag(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "");

  const pathLine = (r) => [r.departmentName, `${r.typeName} (${r.scopeName})`, r.categoryName, r.questionName]
    .filter(Boolean).join(" › ");

  return (
    <PageFrame>
      <PageBody title={title} pageTitle={t("sahaFormFill.fdsSaha2")} />

      <Modal isOpen={open} toggle={requestClose} size="lg" centered scrollable className="modal-fullscreen-sm-down" backdrop="static">
        <ModalHeader toggle={requestClose}>
          <AlertTriangle size={18} className="text-warning me-2" />{title}
        </ModalHeader>

        {result ? (
          <ModalBody className="text-center py-5">
            <CheckCircle size={56} className="mb-3" style={{ color: "var(--vz-green)" }} />
            <h4 className="fw-bold">
              {result === "saved" ? t("uygunsuzlukOlusturPage.kaydedildi") : t("uygunsuzlukOlusturPage.taslakKaydedildi")}
            </h4>
            {result === "draft" && <p className="text-muted">{t("uygunsuzlukOlusturPage.taslakAciklama")}</p>}
            <div className="d-flex gap-2 justify-content-center mt-3">
              <Button color="primary" onClick={resetAll}>{t("uygunsuzlukOlusturPage.yeniUygunsuzluk")}</Button>
              <Button color="light" onClick={leave}>{t("common:kapat")}</Button>
            </div>
          </ModalBody>
        ) : (
          <>
            <ModalBody>
              {error && <Alert color="danger" className="py-2 small" fade={false}>{error}</Alert>}

              {/* Açık taslaklar */}
              {drafts.length > 0 && (
                <div className="border rounded-3 p-2 mb-3 bg-light">
                  <div className="small fw-semibold mb-1">{t("uygunsuzlukOlusturPage.taslaklar", { count: drafts.length })}</div>
                  {drafts.map((d) => (
                    <div key={d.id} className="d-flex align-items-center gap-2 small py-1">
                      <span className="flex-grow-1 text-muted">{fmtDate(d.createdAt)}</span>
                      <Button size="sm" color="primary" outline onClick={() => resumeDraft(d)}>{t("uygunsuzlukOlusturPage.devamEt")}</Button>
                      <Button size="sm" color="light" className="border" onClick={() => dropDraft(d)}>{t("common:sil")}</Button>
                    </div>
                  ))}
                </div>
              )}

              {/* 1 · Fotoğraflar */}
              <div className="mb-4">
                <div className="small fw-semibold text-uppercase text-muted mb-2">{t("uygunsuzlukOlusturPage.fotograflar")}</div>
                <div className="d-flex gap-2">
                  <Button color="primary" className="flex-fill py-3 d-flex align-items-center justify-content-center gap-2"
                          disabled={!!busy} onClick={() => cameraRef.current?.click()}>
                    <Camera size={20} />{t("uygunsuzlukOlusturPage.fotografCek")}
                  </Button>
                  <Button color="light" className="border flex-fill py-3 d-flex align-items-center justify-content-center gap-2"
                          disabled={!!busy} onClick={() => galleryRef.current?.click()}>
                    <ImageIcon size={20} />{t("uygunsuzlukOlusturPage.galeridenSec")}
                  </Button>
                </div>
                {(uploaded.length > 0 || staged.length > 0) ? (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {uploaded.map((p, i) => (
                      <div key={p.id || i} className="rounded border overflow-hidden bg-light d-flex align-items-center justify-content-center"
                           style={{ width: 72, height: 72 }} title={t("uygunsuzlukOlusturPage.kayitliFotograf")}>
                        {getPhotoUrl(p)
                          ? <img src={getPhotoUrl(p)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <ImageIcon size={22} className="text-muted" />}
                      </div>
                    ))}
                    {staged.map((p) => (
                      <div key={p.key} className="position-relative rounded border overflow-hidden" style={{ width: 72, height: 72 }}>
                        <img src={p.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <button type="button" className="btn btn-dark btn-sm position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
                                style={{ width: 24, height: 24, borderRadius: "0 0 0 6px" }} disabled={!!busy}
                                title={t("uygunsuzlukOlusturPage.fotografKaldir")} onClick={() => removeStaged(p.key)}>
                          <XIcon size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small mt-2">{t("uygunsuzlukOlusturPage.fotografIpucu")}</div>
                )}
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden
                       onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                <input ref={galleryRef} type="file" accept="image/*" multiple hidden
                       onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              </div>

              {/* 2 · Birim */}
              <div className="mb-4">
                <div className="small fw-semibold text-uppercase text-muted mb-2">{t("uygunsuzlukOlusturPage.birim")}</div>
                {paths === null ? (
                  <Spinner size="sm" color="primary" />
                ) : loadError ? (
                  <Alert color="danger" className="py-2 small mb-0" fade={false}>{t("uygunsuzlukOlusturPage.katalogYuklenemedi")}</Alert>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {departments.map((d) => (
                      <button key={d.id} type="button"
                              className={`btn btn-sm ${activeDept === d.id ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => pickDept(d.id)}>
                        {d.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 3 · Form Türü (Kapsam) / Uygunsuzluk */}
              {paths !== null && !loadError && (
                <div className="mb-4">
                  <div className="small fw-semibold text-uppercase text-muted mb-2">
                    {hazard ? t("uygunsuzlukOlusturPage.uygunsuzluk") : t("uygunsuzlukOlusturPage.formTuru")}
                  </div>

                  {hazard ? (
                    <div className="border border-warning rounded-3 p-2 bg-warning bg-opacity-10 d-flex align-items-start gap-2">
                      <AlertTriangle size={16} className="text-warning mt-1 flex-shrink-0" />
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">{hazard.hazardName}</div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>{pathLine(hazard)}</div>
                      </div>
                      <Button size="sm" color="link" className="p-0" onClick={() => setHazard(null)}>{t("sahaFormFill.degistir")}</Button>
                    </div>
                  ) : (
                    <>
                      <InputGroup className="mb-2">
                        <span className="input-group-text bg-white border-end-0"><Search size={15} className="text-muted" /></span>
                        <Input className="border-start-0" value={query} onChange={(e) => setQuery(e.target.value)}
                               placeholder={t("uygunsuzlukOlusturPage.araPlaceholder")} />
                      </InputGroup>

                      {searching ? (
                        hits.length === 0 ? (
                          <div className="text-muted small py-2">{t("sahaFormFill.sonucBulunamadi")}</div>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            {hits.slice(0, MAX_HITS).map((r) => (
                              <button key={r.hazardId} type="button" className="btn btn-light border text-start p-2"
                                      onClick={() => pickHazard(r)}>
                                <div className="fw-semibold small">{r.hazardName}</div>
                                <div className="text-muted" style={{ fontSize: "0.72rem" }}>{pathLine(r)}</div>
                              </button>
                            ))}
                            {hits.length > MAX_HITS && (
                              <div className="text-muted small py-1">
                                {t("uygunsuzlukOlusturPage.dahaFazlaSonuc", { count: hits.length - MAX_HITS })}
                              </div>
                            )}
                          </div>
                        )
                      ) : type ? (
                        <>
                          <div className="d-flex align-items-center gap-2 mb-2 small">
                            <span className="fw-semibold">{type.typeName} <span className="text-muted fw-normal">({type.scopeName})</span></span>
                            <Button size="sm" color="link" className="p-0" onClick={() => { setType(null); setTarget(null); }}>
                              {t("sahaFormFill.degistir")}
                            </Button>
                          </div>
                          {typeGroups.map((cat) => (
                            <div key={cat.id} className="mb-2">
                              <div className="small fw-semibold text-muted">{cat.name}</div>
                              {cat.questions.map((q) => (
                                <div key={q.id} className="ms-2 mb-1">
                                  <div className="text-muted" style={{ fontSize: "0.72rem" }}>{q.name}</div>
                                  {q.hazards.map((h) => (
                                    <button key={h.hazardId} type="button" className="btn btn-light border text-start w-100 p-2 mb-1 small"
                                            onClick={() => pickHazard(h)}>
                                      {h.hazardName}
                                    </button>
                                  ))}
                                </div>
                              ))}
                            </div>
                          ))}
                        </>
                      ) : activeDept != null ? (
                        <div className="d-flex flex-column gap-1">
                          {types.map((ty) => (
                            <button key={ty.typeId} type="button" className="btn btn-light border text-start p-2 small"
                                    onClick={() => pickType(ty)}>
                              <span className="fw-semibold">{ty.typeName}</span>{" "}
                              <span className="text-muted">({ty.scopeName})</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted small">{t("uygunsuzlukOlusturPage.birimIpucu")}</div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* 4 · Hedef: stand / salon */}
              {type && (
                <div className="mb-2">
                  <div className="small fw-semibold text-uppercase text-muted mb-2">
                    {t("uygunsuzlukOlusturPage.hedefSecin", { hedef: entityLabel })}
                  </div>
                  {activeTarget ? (
                    <div className="border rounded-3 p-2 d-flex align-items-center gap-2">
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">{activeTarget.name}</div>
                        {(activeTarget.firma || activeTarget.salon) && (
                          <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                            {[activeTarget.firma, activeTarget.salon].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                      {entity !== "fuar" && (
                        <Button size="sm" color="link" className="p-0" onClick={() => setTarget(null)}>{t("sahaFormFill.degistir")}</Button>
                      )}
                    </div>
                  ) : entityList == null ? (
                    <Spinner size="sm" color="primary" />
                  ) : (
                    <>
                      <InputGroup className="mb-2">
                        <span className="input-group-text bg-white border-end-0"><Search size={15} className="text-muted" /></span>
                        <Input className="border-start-0" value={targetQ} onChange={(e) => setTargetQ(e.target.value)}
                               placeholder={t("sahaFormFill.ara")} />
                      </InputGroup>
                      <div className="d-flex flex-column gap-1" style={{ maxHeight: 260, overflowY: "auto" }}>
                        {shownTargets.length === 0 && <div className="text-muted small py-2">{t("sahaFormFill.sonucBulunamadi")}</div>}
                        {shownTargets.slice(0, MAX_TARGETS).map((x) => (
                          <button key={x.id} type="button" className="btn btn-light border text-start p-2"
                                  onClick={() => setTarget(x)}>
                            <div className="fw-semibold small">{x.name}</div>
                            {(x.firma || x.salon) && (
                              <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                                {[x.firma, x.salon].filter(Boolean).join(" · ")}
                              </div>
                            )}
                          </button>
                        ))}
                        {shownTargets.length > MAX_TARGETS && (
                          <div className="text-muted small py-1">
                            {t("uygunsuzlukOlusturPage.dahaFazlaSonuc", { count: shownTargets.length - MAX_TARGETS })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </ModalBody>

            <ModalFooter className="d-flex gap-2">
              {busy && (
                <span className="small text-muted me-auto d-flex align-items-center gap-2">
                  <Spinner size="sm" />
                  {busy.total > 0
                    ? t("uygunsuzlukOlusturPage.yukleniyor", { done: busy.done, total: busy.total })
                    : t("common:yukleniyor")}
                </span>
              )}
              <Button color="light" className="border" disabled={!canDraft} onClick={() => persist(false)}>
                {t("uygunsuzlukOlusturPage.taslakKaydet")}
              </Button>
              <Button color="primary" disabled={!canSave} onClick={() => persist(true)}>
                {t("common:kaydet")}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* Kaydedilmemiş fotoğraflarla kapatma */}
      <Modal isOpen={confirmClose} toggle={() => setConfirmClose(false)} centered size="sm">
        <ModalHeader toggle={() => setConfirmClose(false)}>{t("uygunsuzlukOlusturPage.kapatBaslik")}</ModalHeader>
        <ModalBody className="small">{t("uygunsuzlukOlusturPage.kapatAciklama", { count: staged.length })}</ModalBody>
        <ModalFooter className="d-flex flex-column align-items-stretch gap-2">
          <Button color="primary" onClick={async () => { setConfirmClose(false); if (await persist(false)) leave(); }}>
            {t("uygunsuzlukOlusturPage.taslakKaydet")}
          </Button>
          <Button color="light" className="border" onClick={() => { setConfirmClose(false); leave(); }}>
            {t("uygunsuzlukOlusturPage.fotograflariAt")}
          </Button>
          <Button color="link" onClick={() => setConfirmClose(false)}>{t("uygunsuzlukOlusturPage.geriDon")}</Button>
        </ModalFooter>
      </Modal>
    </PageFrame>
  );
}

export default UygunsuzlukOlusturPage;
